import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

function ManufacturingDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalItems: 0,
    activeWorkOrders: 0,
    lowStockItems: 0,
    completedToday: 0
  });
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageTitle('Dashboard');

  useEffect(() => {
    fetchDashboardData();
  }, []);


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use optimized endpoints
      const [itemCountRes, workOrdersRes, stockStatsRes] = await Promise.all([
        manufacturingAPI.items.getCount(), // New endpoint for just count
        manufacturingAPI.workOrders.list({ status: 'active', page_size: 5 }), // Only get 5
        manufacturingAPI.stockLevels.dashboardStats(), // New optimized endpoint
      ]);

      setStats({
        totalItems: itemCountRes.data.count || 0,
        activeWorkOrders: workOrdersRes.data.count || 0,
        lowStockItems: stockStatsRes.data.low_stock_count || 0,
        completedToday: 0
      });

      setRecentWorkOrders(workOrdersRes.data.results || []);
      setLowStockItems(stockStatsRes.data.low_stock_items || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'default',
      'released': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const StatCard = ({ title, value, color = 'primary', action }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom variant="h6">
          {title}
        </Typography>
        <Typography variant="h3" color={color}>
          {value}
        </Typography>
        {action && (
          <Box mt={2}>
            <Button size="small" onClick={action.onClick}>
              {action.label}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }} textAlign="center">
          Loading manufacturing dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manufacturing Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Items" 
            value={stats.totalItems}
            action={{ label: "Manage Items", onClick: () => navigate('/manufacturing/items') }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Active Work Orders" 
            value={stats.activeWorkOrders}
            color="warning"
            action={{ label: "View Work Orders", onClick: () => navigate('/manufacturing/work-orders') }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Low Stock Items" 
            value={stats.lowStockItems}
            color="error"
            action={{ label: "View Inventory", onClick: () => navigate('/manufacturing/inventory') }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Completed Today" 
            value={stats.completedToday}
            color="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Active Work Orders */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Work Orders
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Work Order</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Priority</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentWorkOrders.slice(0, 5).map((wo) => (
                      <TableRow key={wo.id}>
                        <TableCell>{wo.work_order_number}</TableCell>
                        <TableCell>{wo.item_display}</TableCell>
                        <TableCell>
                          <Chip 
                            label={wo.status} 
                            color={getStatusColor(wo.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={wo.progress_percentage || 0} 
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="text.secondary">
                                {Math.round(wo.progress_percentage || 0)}%
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`Priority ${wo.priority}`}
                            color={wo.priority <= 2 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {recentWorkOrders.length === 0 && (
                <Box textAlign="center" py={3}>
                  <Typography color="textSecondary">
                    No active work orders
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/manufacturing/work-orders')}
                  >
                    Create Work Order
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Low Stock Items */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Low Stock Alert
              </Typography>
              {lowStockItems.length > 0 ? (
                lowStockItems.map((stock) => (
                  <Box key={stock.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.item_display}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Available: {stock.quantity_available} {stock.item?.unit_of_measure || 'EA'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((stock.quantity_available / (stock.item?.reorder_level || 1)) * 100, 100)}
                      color="error"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ))
              ) : (
                <Typography color="textSecondary">
                  All items adequately stocked
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item>
          <Button 
            variant="contained" 
            onClick={() => navigate('/manufacturing/items')}
          >
            Manage Items
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="contained" 
            onClick={() => navigate('/manufacturing/operations')}
          >
            Manage Operations
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/manufacturing/bom')}
          >
            Build BOMs
          </Button>
        </Grid>
        <Grid item>
            <Button 
                variant="contained" 
                onClick={() => navigate('/manufacturing/inventory')}
                >
                Inventory Management
                </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/shop-floor')}
          >
            Shop Floor Terminal
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ManufacturingDashboard;